/**
 * Queue Edit — full-page form to edit a pending create_asset queue item.
 * Accessible via #/queue-edit/:id from the Edit button in the queue sheet.
 */

import { router } from '../router.js';
import { getItem, updatePayload } from '../db.js';
import { showToast } from '../toast.js';
import { hapticSuccess } from '../haptics.js';

export async function renderQueueEdit(id) {
  const numId = parseInt(id, 10);
  const app = document.getElementById('app');

  app.innerHTML = `
    <div class="page">
      <div class="page-header">
        <button class="back-btn" id="back-btn" aria-label="Back">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="15 18 9 12 15 6"/></svg>
        </button>
        <h1>Edit Item</h1>
      </div>
      <div id="edit-body">
        <div style="text-align:center; padding:40px;">
          <div class="spinner"></div>
        </div>
      </div>
    </div>
  `;

  document.getElementById('back-btn').addEventListener('click', () => router.navigate('/'));

  const item = await getItem(numId);

  if (!item) {
    document.getElementById('edit-body').innerHTML =
      `<p style="text-align:center; padding:24px; color:var(--color-text-muted);">Item not found or already synced.</p>`;
    return;
  }

  if (item.type !== 'create_asset') {
    document.getElementById('edit-body').innerHTML = `
      <div class="card">
        <p style="font-size:0.9rem; color:var(--color-text-muted);">
          Only <strong>Add Asset</strong> queue items can be edited. This item is a <strong>${item.type.replace(/_/g, ' ')}</strong> operation.
        </p>
      </div>
    `;
    return;
  }

  const p = item.payload;

  document.getElementById('edit-body').innerHTML = `
    <form id="edit-form">
      ${p.model_name ? `
        <div class="form-group">
          <label>Model</label>
          <div style="padding: 11px 13px; background: var(--color-surface-raised); border: 1px solid var(--color-border); border-radius: var(--radius-sm); font-size: 1rem; color: var(--color-text-muted);">
            ${escapeHtml(p.model_name)}
          </div>
        </div>
      ` : ''}

      <div class="form-group">
        <label for="edit-name">Name</label>
        <input type="text" id="edit-name" class="form-control" value="${escapeAttr(p.name || '')}" autocomplete="off" />
      </div>

      <div style="display:grid; grid-template-columns:1fr 1fr; gap:12px;">
        <div class="form-group">
          <label for="edit-tag">Asset Tag</label>
          <input type="text" id="edit-tag" class="form-control mono" value="${escapeAttr(p.asset_tag || '')}" autocomplete="off" />
        </div>
        <div class="form-group">
          <label for="edit-serial">Serial</label>
          <input type="text" id="edit-serial" class="form-control mono" value="${escapeAttr(p.serial || '')}" autocomplete="off" />
        </div>
      </div>

      <div class="form-group">
        <label for="edit-wlan">WLAN MAC</label>
        <input type="text" id="edit-wlan" class="form-control mono" value="${escapeAttr(p[wlanKey(p)] || '')}" placeholder="AA:BB:CC:DD:EE:FF" autocomplete="off" />
      </div>

      <div class="form-group">
        <label for="edit-lan">LAN MAC</label>
        <input type="text" id="edit-lan" class="form-control mono" value="${escapeAttr(p[lanKey(p)] || '')}" placeholder="AA:BB:CC:DD:EE:FF" autocomplete="off" />
      </div>

      <div class="form-group" style="margin-bottom:0;">
        <label for="edit-notes">Notes</label>
        <textarea id="edit-notes" class="form-control" rows="3" style="resize:none;">${escapeHtml(p.notes || '')}</textarea>
      </div>

      <div style="display:flex; gap:8px; margin-top:24px;">
        <button type="button" class="btn btn-secondary" style="flex:1;" id="btn-cancel">Cancel</button>
        <button type="submit" class="btn btn-primary" style="flex:1;">Save Changes</button>
      </div>
    </form>
  `;

  document.getElementById('btn-cancel').addEventListener('click', () => router.navigate('/'));

  document.getElementById('edit-form').addEventListener('submit', async (e) => {
    e.preventDefault();
    const submit = e.target.querySelector('[type="submit"]');
    submit.disabled = true;
    submit.textContent = 'Saving...';

    const updated = { ...p };
    updated.name = document.getElementById('edit-name').value.trim();
    updated.asset_tag = document.getElementById('edit-tag').value.trim();
    updated.serial = document.getElementById('edit-serial').value.trim();
    updated.notes = document.getElementById('edit-notes').value.trim();

    const wlan = document.getElementById('edit-wlan').value.trim();
    const lan = document.getElementById('edit-lan').value.trim();

    // Preserve MAC column keys from original payload
    const wk = wlanKey(p);
    const lk = lanKey(p);
    if (wlan) updated[wk] = wlan; else delete updated[wk];
    if (lan) updated[lk] = lan; else delete updated[lk];

    try {
      await updatePayload(numId, updated);
      hapticSuccess();
      showToast('Changes saved');
      window.dispatchEvent(new CustomEvent('queue-updated'));
      router.navigate('/');
    } catch (err) {
      showToast('Save failed: ' + err.message);
      submit.disabled = false;
      submit.textContent = 'Save Changes';
    }
  });
}

// Detect which MAC column keys are used in the payload (they're hardcoded constants
// from settings.js but we don't import them here — detect by key name pattern instead
// so this component stays decoupled from settings).
function wlanKey(payload) {
  return Object.keys(payload).find((k) => k.includes('wlan')) || '_snipeit_wlan_mac_address_6';
}

function lanKey(payload) {
  return Object.keys(payload).find((k) => k.includes('mac_address_1')) || '_snipeit_mac_address_1';
}

function escapeHtml(str) {
  const div = document.createElement('div');
  div.textContent = str;
  return div.innerHTML;
}

function escapeAttr(str) {
  return (str || '').replace(/"/g, '&quot;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}
